import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  ChevronDown,
  Menu,
  X,
  Calculator,
  Search,
  MessageSquare,
  FileText,
  Wrench,
  LayoutDashboard,
  User,
  LogOut,
  Sparkles,
  Shield,
  HelpCircle,
  Briefcase,
  BarChart3,
  Users,
  UsersRound,
  Moon,
  Sun,
  Scale,
  Settings,
  HardHat,
  Gift,
  Award,
  FileSignature,
  Building2,
  Headphones,
  CreditCard,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import NotificationCenter from './NotificationCenter';
import TutorialButton from './TutorialButton';
import LanguageSelector from '../LanguageSelector';

const Navbar = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;

  // Handle logout with navigation to home
  const handleLogout = () => {
    // Navigate first, then logout to avoid protected route redirect
    navigate('/');
    // Small delay to ensure navigation starts before auth state clears
    setTimeout(() => {
      logout();
    }, 100);
  };

  const navLinks = [
    { path: '/analyzer', label: t('nav.dealAnalyzer', 'Deal Analyzer'), icon: Calculator },
    { path: '/comps', label: t('nav.compFinder', 'Find Comps'), icon: Search },
    { path: '/repair-estimator', label: t('nav.repairEstimator', 'Repair Costs'), icon: Wrench },
    { path: '/chat', label: t('nav.aiAssistant', 'AI Assistant'), icon: MessageSquare },
    { path: '/projects', label: t('nav.projects', 'Project Manager'), icon: LayoutDashboard },
    { path: '/funding', label: t('nav.funding', 'Lender Funding'), icon: FileText },
    { path: '/marketing', label: t('nav.marketing', 'Deal Marketing'), icon: Sparkles },
    { path: '/wholesale', label: t('nav.wholesale', 'Wholesale Hub'), icon: Briefcase },
    { path: '/contracts', label: t('nav.contracts', 'Contracts'), icon: Scale },
    { path: '/contractors', label: t('nav.contractors', 'Contractors'), icon: HardHat },
    { path: '/crm', label: t('nav.crm', 'Investor CRM'), icon: Users },
    { path: '/esign', label: t('nav.esign', 'E-Sign'), icon: FileSignature },
    { path: '/directory', label: t('nav.directory', 'Directory'), icon: Building2 },
    { path: '/teams', label: t('nav.teams', 'Teams'), icon: UsersRound },
  ];

  const authenticatedLinks = [
    { path: '/pipeline', label: t('nav.pipeline', 'Pipeline'), icon: BarChart3 },
    { path: '/my-deals', label: t('nav.myDeals', 'My Deals'), icon: LayoutDashboard },
    { path: '/compare', label: t('nav.compareDeals', 'Compare Deals'), icon: Scale },
    { path: '/analytics', label: t('nav.analytics', 'Analytics'), icon: BarChart3 },
    { path: '/teams', label: t('nav.teams', 'Teams'), icon: UsersRound },
    { path: '/crm', label: t('nav.crm', 'CRM'), icon: Users },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-purple-100 dark:border-purple-900/50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="nav-logo">
            <img 
              src="/logo.png" 
              alt="PropertyARV" 
              className="h-16 w-auto object-contain dark:brightness-110"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {!isAuthenticated && (
              <Link to="/pricing">
                <Button variant="ghost" className={`text-gray-600 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-400 ${isActive('/pricing') ? 'text-purple-600 dark:text-pink-400' : ''}`}>
                  {t('nav.pricing', 'Pricing')}
                </Button>
              </Link>
            )}

            <Link to="/blog">
              <Button variant="ghost" className={`text-gray-600 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-400 ${isActive('/blog') ? 'text-purple-600 dark:text-pink-400' : ''}`} data-testid="nav-blog-btn">
                Blog
              </Button>
            </Link>

            {!isAuthenticated && (
              <Link to="/directory">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-md" data-testid="nav-directory-btn">
                  <Building2 className="w-4 h-4 mr-1.5" />
                  {t('nav.directory', 'Directory')}
                </Button>
              </Link>
            )}

            <Link to="/help" data-testid="help-link">
              <Button variant="ghost" className={`text-gray-600 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-400 ${isActive('/help') ? 'text-purple-600 dark:text-pink-400' : ''}`}>
                <HelpCircle className="w-4 h-4 mr-1" />
                {t('nav.help', 'Help')}
              </Button>
            </Link>

            {isAuthenticated && (
              <Link to="/support" data-testid="support-link">
                <Button variant="ghost" className={`text-gray-600 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-400 ${isActive('/support') ? 'text-purple-600 dark:text-pink-400' : ''}`}>
                  <Headphones className="w-4 h-4 mr-1" />
                  Support
                </Button>
              </Link>
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-2">
            {/* Tutorial Button */}
            <TutorialButton />
            
            {/* Language Selector */}
            <LanguageSelector variant="compact" />
            
            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-gray-600 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-400"
              data-testid="theme-toggle"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {isAuthenticated ? (
              <>
                <NotificationCenter />
                {user?.isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" className="text-purple-600 dark:text-pink-400 hover:text-purple-700 dark:hover:text-purple-300">
                      <Shield className="w-4 h-4 mr-2" />
                      {t('nav.admin', 'Admin')}
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-purple-200 dark:border-pink-700 dark:text-gray-200" data-testid="user-menu-trigger">
                      <User className="w-4 h-4 mr-2" />
                      {user?.name?.split(' ')[0] || t('nav.account', 'Account')}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                      {user?.email}
                      {user?.isAdmin && (
                        <Badge className="ml-2 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-pink-400 border-0 text-xs">{t('nav.admin')}</Badge>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    {user?.isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                          <Shield className="w-4 h-4" />
                          {t('nav.adminPanel', 'Admin Panel')}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/teams" className="flex items-center gap-2 cursor-pointer">
                        <UsersRound className="w-4 h-4" />
                        {t('nav.teamSettings', 'Team Settings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        {t('nav.settings', 'Settings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/billing" className="flex items-center gap-2 cursor-pointer" data-testid="billing-nav-link">
                        <CreditCard className="w-4 h-4" />
                        Billing
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/referrals" className="flex items-center gap-2 cursor-pointer">
                        <Gift className="w-4 h-4 text-purple-500" />
                        <span>{t('nav.referrals', 'Earn $50')}</span>
                        <Badge className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] border-0 px-1.5 py-0">NEW</Badge>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/analyzer" className="flex items-center gap-2 cursor-pointer">
                        <Calculator className="w-4 h-4" />
                        {t('nav.dealAnalyzer', 'Deal Analyzer')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/compare" className="flex items-center gap-2 cursor-pointer">
                        <Scale className="w-4 h-4" />
                        {t('nav.compareDeals', 'Compare Deals')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/directory/my-listings" className="flex items-center gap-2 cursor-pointer">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        {t('nav.myListings', 'My Listings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.signOut', 'Sign Out')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-600 hover:text-purple-700" data-testid="login-nav-btn">
                    {t('nav.signIn', 'Sign In')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all" data-testid="register-nav-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('nav.getStarted', 'Get Started')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-purple-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t border-purple-100 dark:border-purple-900/50 absolute top-16 left-0 right-0 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {isAuthenticated && (
              <>
                {/* Account Info */}
                <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 mb-2 pb-3">
                  {t('nav.signedInAs', 'Signed in as')} <span className="font-medium text-gray-700 dark:text-gray-300">{user?.name}</span>
                  {user?.isAdmin && (
                    <Badge className="ml-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-pink-400 border-0 text-xs">{t('nav.admin1')}</Badge>
                  )}
                </div>

                {/* Dashboard */}
                <Link
                  to="/dashboard"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                  Dashboard
                </Link>

                {/* Navigation Links */}
                <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Tools</p>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <link.icon className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    {link.label}
                  </Link>
                ))}

                {/* Account & Settings Section */}
                <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-2">
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Account</p>
                  <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <Settings className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    {t('nav.settings', 'Settings')}
                  </Link>
                  <Link to="/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <CreditCard className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    Billing
                  </Link>
                  <Link to="/teams" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <UsersRound className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    {t('nav.teamSettings', 'Team Settings')}
                  </Link>
                  <Link to="/referrals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <Gift className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    {t('nav.referrals', 'Earn $50')}
                    <Badge className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] border-0 px-1.5 py-0">NEW</Badge>
                  </Link>
                  <Link to="/directory/my-listings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <Building2 className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                    {t('nav.myListings', 'My Listings')}
                  </Link>
                </div>

                {/* Admin & Logout */}
                <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-2 space-y-1">
                  {user?.isAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-purple-300 dark:border-pink-700 text-purple-700 dark:text-pink-400 hover:bg-purple-50 dark:hover:bg-purple-900/30">
                        <Shield className="w-4 h-4 mr-2" />
                        {t('nav.adminPanel', 'Admin Panel')}
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="ghost" 
                    className="w-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.signOut', 'Sign Out')}
                  </Button>
                </div>
              </>
            )}

            {!isAuthenticated && (
              <>
                <Link
                  to="/pricing"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FileText className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                  {t('nav.pricing', 'Pricing')}
                </Link>
                <Link
                  to="/directory"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-directory"
                >
                  <Building2 className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                  {t('nav.directory', 'Directory')}
                </Link>
                <Link
                  to="/blog"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-blog"
                >
                  <FileText className="w-5 h-5 text-purple-600 dark:text-pink-400" />
                  Blog
                </Link>
                <div className="pt-4 border-t border-purple-100 dark:border-purple-900/50 space-y-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-purple-200 dark:border-pink-700 text-purple-700 dark:text-pink-400 hover:bg-purple-50 dark:hover:bg-purple-900/30">
                      {t('nav.signIn', 'Sign In')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white">
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('nav.getStarted', 'Get Started')}
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
